import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';

import { useAdminTeams } from '@/api-models/admin/teams';
import { tierLabel } from '@/api-models/balance';
import { formatUsd } from '@/api-models/usage';
import { formatDate } from '@/features/admin/format';
import { useDebouncedValue } from '@/features/admin/hooks/useDebouncedValue';
import { Badge } from '@/ui/Badge';
import { Card } from '@/ui/Card';
import { Input } from '@/ui/Input';
import { Pagination } from '@/ui/Pagination';
import { SpinnerBlock } from '@/ui/Spinner';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/ui/Table';

const PAGE_SIZE = 25;

export function AdminTeamsPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebouncedValue(searchInput, 250);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const query = useMemo(
    () => ({ search: debouncedSearch || undefined, page, pageSize: PAGE_SIZE }),
    [debouncedSearch, page],
  );

  const { teams, total, isLoading } = useAdminTeams(query);

  return (
    <>
      <header>
        <h1 className="page-title">Teams</h1>
        <p className="page-subtitle">All teams on the platform.</p>
      </header>

      <div className="flex flex-col gap-4">
        <Input
          inputSize={8}
          className="max-w-sm"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name or slug…"
          iconLeft={<MagnifyingGlassIcon size={14} />}
        />

        <Card padding="none" radius="2xl" className="overflow-hidden">
          {isLoading && teams.length === 0 ? (
            <div className="py-12">
              <SpinnerBlock message="Loading teams…" />
            </div>
          ) : teams.length === 0 ? (
            <p className="p-8 text-center text-xs text-fg-muted">No teams match.</p>
          ) : (
            <Table>
              <TableHead>
                <tr>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Slug</TableHeaderCell>
                  <TableHeaderCell className="w-24">Tier</TableHeaderCell>
                  <TableHeaderCell className="w-28 text-right">Available</TableHeaderCell>
                  <TableHeaderCell className="w-28 text-right">Members</TableHeaderCell>
                  <TableHeaderCell className="w-28 text-right">Repos</TableHeaderCell>
                  <TableHeaderCell className="w-30 text-right">Workspaces</TableHeaderCell>
                  <TableHeaderCell className="w-36">Created</TableHeaderCell>
                </tr>
              </TableHead>
              <TableBody>
                {teams.map((t) => (
                  <TableRow key={t.id} clickable onClick={() => navigate(`/admin/teams/${t.id}`)}>
                    <TableCell className="text-xs font-medium text-fg">
                      <span className="inline-flex items-center gap-2">
                        {t.name}
                        {t.is_personal && <Badge>Personal</Badge>}
                      </span>
                    </TableCell>
                    <TableCell className="text-2xs text-fg-muted">{t.slug}</TableCell>
                    <TableCell className="text-2xs">{tierLabel(t.license_tier)}</TableCell>
                    <TableCell
                      className={`text-right text-xs tabular-nums ${t.is_exhausted ? 'font-medium text-danger' : ''}`}
                    >
                      {formatUsd(t.available_micro_usd)}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{t.member_count}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{t.repository_count}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{t.workspace_count}</TableCell>
                    <TableCell className="whitespace-nowrap text-2xs text-fg-muted tabular-nums">
                      {formatDate(t.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
      </div>
    </>
  );
}
